import { useState, useEffect, useCallback } from 'react'
import { useNavigate } from 'react-router-dom'
import axios from 'axios'
import { format, parseISO, differenceInDays } from 'date-fns'
import { es, enUS } from 'date-fns/locale'
import { useLanguage } from '../context/LanguageContext'
import { Card, CardContent } from './ui/card'
import { Badge } from './ui/badge'
import { cn } from '../lib/utils'
import {
  Bell, CreditCard, Car, Home, Wrench, Building2, MoreHorizontal,
  ChevronRight, AlertTriangle, Clock, Calendar
} from 'lucide-react'

const API = `${process.env.REACT_APP_BACKEND_URL}/api`

// Category icons
const categoryIcons = {
  Tarjeta: CreditCard,
  Auto: Car,
  Casa: Home,
  Servicios: Wrench,
  Gobierno: Building2,
  Otros: MoreHorizontal
}

export default function RemindersWidget() {
  const { locale } = useLanguage()
  const navigate = useNavigate()
  const lang = locale.startsWith('es') ? 'es' : 'en'
  const dateLocale = lang === 'es' ? es : enUS
  
  const [data, setData] = useState(null)
  const [loading, setLoading] = useState(true)

  const fetchUpcoming = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/reminders/upcoming?days=7`)
      setData(response.data)
    } catch (error) {
      console.error('Error fetching upcoming reminders:', error)
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => {
    fetchUpcoming()
  }, [fetchUpcoming])

  if (loading || !data || data.total === 0) {
    return null // Don't show widget if no upcoming reminders
  }

  const { reminders, stats } = data

  return (
    <Card 
      className="bg-gradient-to-br from-amber-50 to-orange-50 dark:from-amber-950/30 dark:to-orange-950/30 border-amber-200/50 dark:border-amber-800/30 cursor-pointer hover:shadow-md transition-all"
      onClick={() => navigate('/recordatorios')}
      data-testid="reminders-widget"
    >
      <CardContent className="p-4">
        {/* Header */}
        <div className="flex items-center justify-between mb-3">
          <div className="flex items-center gap-2">
            <div className="w-8 h-8 rounded-full bg-amber-500/20 flex items-center justify-center">
              <Bell className="w-4 h-4 text-amber-600 dark:text-amber-400" />
            </div>
            <div>
              <h3 className="font-semibold text-sm">
                {lang === 'es' ? 'Próximos 7 días' : 'Next 7 days'}
              </h3>
              <p className="text-xs text-muted-foreground">
                {data.total} {lang === 'es' ? 'recordatorio(s)' : 'reminder(s)'}
              </p>
            </div>
          </div>
          
          {/* Urgency Badges */}
          <div className="flex gap-1">
            {stats.vencidos > 0 && (
              <Badge className="bg-red-500 text-white text-xs px-2 py-0.5">
                {stats.vencidos} {lang === 'es' ? 'vencido' : 'overdue'}
              </Badge>
            )}
            {stats.hoy > 0 && (
              <Badge className="bg-amber-500 text-white text-xs px-2 py-0.5">
                {stats.hoy} {lang === 'es' ? 'hoy' : 'today'}
              </Badge>
            )}
          </div>
        </div>
        
        {/* Reminders List (max 3) */}
        <div className="space-y-2">
          {reminders.slice(0, 3).map(reminder => {
            const CategoryIcon = categoryIcons[reminder.category] || MoreHorizontal
            const daysUntil = reminder.days_until_due
            
            // Determine urgency style
            let urgencyColor = 'text-blue-600 dark:text-blue-400'
            let bgColor = 'bg-blue-100 dark:bg-blue-900/30'
            
            if (daysUntil < 0) {
              urgencyColor = 'text-red-600 dark:text-red-400'
              bgColor = 'bg-red-100 dark:bg-red-900/30'
            } else if (daysUntil === 0) {
              urgencyColor = 'text-amber-600 dark:text-amber-400'
              bgColor = 'bg-amber-100 dark:bg-amber-900/30'
            } else if (daysUntil <= 3) {
              urgencyColor = 'text-orange-600 dark:text-orange-400'
              bgColor = 'bg-orange-100 dark:bg-orange-900/30'
            }
            
            return (
              <div 
                key={reminder.id}
                className={cn(
                  "flex items-center gap-3 p-2 rounded-lg",
                  bgColor
                )}
              >
                <CategoryIcon className={cn("w-4 h-4", urgencyColor)} />
                
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium truncate">{reminder.title}</p>
                  <div className="flex items-center gap-2 text-xs text-muted-foreground">
                    {reminder.amount && (
                      <span className="font-medium">
                        ${reminder.amount.toLocaleString()}
                      </span>
                    )}
                    <span>
                      {reminder.next_due_at && format(parseISO(reminder.next_due_at), 'dd MMM', { locale: dateLocale })}
                    </span>
                  </div>
                </div>
                
                <Badge 
                  variant="outline"
                  className={cn("text-xs px-1.5 py-0.5 border-0", urgencyColor, bgColor)}
                >
                  {daysUntil < 0 
                    ? (lang === 'es' ? `${Math.abs(daysUntil)}d vencido` : `${Math.abs(daysUntil)}d late`)
                    : daysUntil === 0 
                      ? (lang === 'es' ? 'Hoy' : 'Today')
                      : `${daysUntil}d`
                  }
                </Badge>
              </div>
            )
          })}
        </div>
        
        {/* View All Link */}
        {reminders.length > 3 && (
          <div className="flex items-center justify-center mt-3 text-xs text-amber-600 dark:text-amber-400">
            <span>{lang === 'es' ? 'Ver todos' : 'View all'}</span>
            <ChevronRight className="w-3 h-3" />
          </div>
        )}
      </CardContent>
    </Card>
  )
}
